import {
  StreamLanguage,
  type StreamParser,
  type StringStream,
} from "@codemirror/language";

interface JsonataState {
  inBlockComment: boolean;
  lastTokenWasValue: boolean;
}

const jsonataParser: StreamParser<JsonataState> = {
  startState(): JsonataState {
    return { inBlockComment: false, lastTokenWasValue: false };
  },

  token(stream: StringStream, state: JsonataState): string | null {
    // Continue block comment
    if (state.inBlockComment) {
      const end = stream.skipTo("*/");
      if (end) {
        stream.next();
        stream.next();
        state.inBlockComment = false;
      } else {
        stream.skipToEnd();
      }
      state.lastTokenWasValue = false;
      return "comment";
    }

    // Skip whitespace
    if (stream.eatSpace()) return null;

    const ch = stream.peek()!;

    // Block comment start
    if (ch === "/" && stream.match("/*", false)) {
      stream.next();
      stream.next();
      state.inBlockComment = true;
      const end = stream.skipTo("*/");
      if (end) {
        stream.next();
        stream.next();
        state.inBlockComment = false;
      } else {
        stream.skipToEnd();
      }
      state.lastTokenWasValue = false;
      return "comment";
    }

    // Strings (double-quoted)
    if (ch === '"') {
      stream.next();
      while (!stream.eol()) {
        const c = stream.next();
        if (c === "\\") {
          stream.next();
        } else if (c === '"') {
          break;
        }
      }
      // Check if this string is a property key (followed by ":")
      if (stream.match(/^\s*:/, false)) {
        state.lastTokenWasValue = false;
        return "propertyName";
      }
      state.lastTokenWasValue = true;
      return "string";
    }

    // Strings (single-quoted)
    if (ch === "'") {
      stream.next();
      while (!stream.eol()) {
        const c = stream.next();
        if (c === "\\") {
          stream.next();
        } else if (c === "'") {
          break;
        }
      }
      // Check if this string is a property key (followed by ":")
      if (stream.match(/^\s*:/, false)) {
        state.lastTokenWasValue = false;
        return "propertyName";
      }
      state.lastTokenWasValue = true;
      return "string";
    }

    // Backtick-quoted identifiers
    if (ch === "`") {
      stream.next();
      while (!stream.eol()) {
        const c = stream.next();
        if (c === "`") break;
      }
      state.lastTokenWasValue = true;
      return "string-2";
    }

    // Regex literals (only when not after a value token, to disambiguate from division)
    if (ch === "/" && !state.lastTokenWasValue) {
      stream.next();
      let escaped = false;
      while (!stream.eol()) {
        const c = stream.next()!;
        if (escaped) {
          escaped = false;
        } else if (c === "\\") {
          escaped = true;
        } else if (c === "/") {
          stream.eatWhile(/[gimsuy]/);
          state.lastTokenWasValue = true;
          return "string-2";
        }
      }
      state.lastTokenWasValue = true;
      return "string-2";
    }

    // Numbers
    if (/\d/.test(ch) || (ch === "." && stream.match(/^\.\d/, false))) {
      stream.match(/^\d*\.?\d+(?:[eE][+-]?\d+)?/);
      state.lastTokenWasValue = true;
      return "number";
    }

    // Variable references ($name)
    if (ch === "$") {
      stream.next();
      stream.eatWhile(/[\w]/);
      state.lastTokenWasValue = true;
      return "variable-2";
    }

    // Multi-character operators
    if (
      stream.match("~>") ||
      stream.match(":=") ||
      stream.match("!=") ||
      stream.match("<=") ||
      stream.match(">=") ||
      stream.match("**") ||
      stream.match("..")
    ) {
      state.lastTokenWasValue = false;
      return "operator";
    }

    // Single-character operators
    if ("+-*/%=<>&|!^".includes(ch)) {
      stream.next();
      state.lastTokenWasValue = false;
      return "operator";
    }

    // Division (only when after a value)
    if (ch === "/" && state.lastTokenWasValue) {
      stream.next();
      state.lastTokenWasValue = false;
      return "operator";
    }

    // Punctuation and brackets
    if ("()[]{}.,;:?".includes(ch)) {
      stream.next();
      state.lastTokenWasValue = ch === ")" || ch === "]" || ch === "}";
      return "punctuation";
    }

    // Identifiers and keywords
    if (/[a-zA-Z_]/.test(ch)) {
      stream.eatWhile(/[\w]/);
      const word = stream.current();
      const keywords = new Set(["function", "and", "or", "in"]);
      const atoms = new Set(["true", "false", "null"]);

      if (keywords.has(word)) {
        state.lastTokenWasValue = false;
        return "keyword";
      }
      if (atoms.has(word)) {
        state.lastTokenWasValue = true;
        return "atom";
      }
      state.lastTokenWasValue = true;
      return "variable";
    }

    // Fallback: consume character
    stream.next();
    state.lastTokenWasValue = false;
    return null;
  },
};

export const jsonataLanguage = StreamLanguage.define(jsonataParser);
